/**
 * ProjectTimeline Component
 *
 * Main container for the project bitacora/timeline.
 * Displays a chronological feed of all project events.
 *
 * @module components/timeline/ProjectTimeline
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ButtonGroup, Spinner, Alert } from 'react-bootstrap';
import TimelineEvent from './TimelineEvent';
import TimelineFilters from './TimelineFilters';
import TimelineSearchBar from './TimelineSearchBar';
import TimelineExportMenu from './TimelineExportMenu';
import AddManualEventModal from './AddManualEventModal';
import BitacoraFeed from './BitacoraFeed';
import {
  getProjectTimelineDirect,
  getTimelineSummary,
} from '../../services/timelineService';
import { useAuth } from '../../contexts/AuthContext';
import './ProjectTimeline.css';

/**
 * ProjectTimeline component
 * @param {Object} props
 * @param {string} props.projectId - Project ID
 * @param {Object} props.project - Project data (for export)
 * @param {string} [props.highlightedEventId] - Event ID to scroll to and flash
 * @param {Function} [props.onHighlightConsumed] - Called once the highlight has been consumed
 */
const ProjectTimeline = ({ projectId, project, highlightedEventId = null, onHighlightConsumed }) => {
  const { isAdmin, isSupervisor, isTechnician } = useAuth();
  const queryClient = useQueryClient();
  // #331: RLS now allows admin/supervisor (unscoped) and technician (scoped
  // to their own/assigned project) to INSERT into project_history. This
  // gate previously omitted supervisor — the button was invisible even
  // though nothing else about supervisor access ever excluded them from the
  // rest of this project view. Keep in sync with
  // supabase/migrations/20260725150000_project_history_staff_insert_policy.sql.
  const canAddEntries = isAdmin || isSupervisor || isTechnician;

  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('feed'); // 'feed' | 'list' — resets per session per UI convention
  const [filters, setFilters] = useState({
    eventTypes: null,
    startDate: null,
    endDate: null,
    milestonesOnly: false,
  });

  // Clear the highlight after a short delay so the flash animation runs once
  useEffect(() => {
    if (!highlightedEventId || !onHighlightConsumed) return undefined;
    const timer = setTimeout(() => {
      onHighlightConsumed();
    }, 2500);
    return () => clearTimeout(timer);
  }, [highlightedEventId, onHighlightConsumed]);

  // Fetch timeline data
  const {
    data: timelineData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['projectTimeline', projectId, filters],
    queryFn: () => getProjectTimelineDirect(projectId, filters),
    enabled: !!projectId,
    staleTime: 30000, // 30 seconds
  });

  // Fetch summary stats
  const { data: summaryData } = useQuery({
    queryKey: ['timelineSummary', projectId],
    queryFn: () => getTimelineSummary(projectId),
    enabled: !!projectId,
  });

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      eventTypes: null,
      startDate: null,
      endDate: null,
      milestonesOnly: false,
    });
    setSearchTerm('');
  }, []);

  const handleAddSuccess = useCallback(() => {
    refetch();
    queryClient.invalidateQueries(['timelineSummary', projectId]);
  }, [refetch, queryClient, projectId]);

  // Filter events by search term
  const filteredEvents = React.useMemo(() => {
    if (!timelineData?.data?.events) return [];
    if (!searchTerm) return timelineData.data.events;

    const searchLower = searchTerm.toLowerCase();
    return timelineData.data.events.filter(event =>
      event.description?.toLowerCase().includes(searchLower) ||
      event.user_name?.toLowerCase().includes(searchLower)
    );
  }, [timelineData?.data?.events, searchTerm]);

  // Group by date for display
  const groupedEvents = React.useMemo(() => {
    const grouped = {};
    filteredEvents.forEach(event => {
      const date = new Date(event.timestamp);
      const dateKey = date.toISOString().split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          displayDate: formatDateHeader(date),
          events: [],
        };
      }
      grouped[dateKey].events.push(event);
    });
    return Object.values(grouped).sort((a, b) =>
      new Date(b.date) - new Date(a.date)
    );
  }, [filteredEvents]);

  const summary = summaryData?.data;

  // Loading state
  if (isLoading) {
    return (
      <div className="timeline-loading">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando bitácora...</span>
        </Spinner>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error al cargar la bitácora</Alert.Heading>
        <p>{error.message}</p>
        <Button variant="outline-danger" size="sm" onClick={() => refetch()}>
          <i className="bi bi-arrow-clockwise me-1"></i>
          Reintentar
        </Button>
      </Alert>
    );
  }

  return (
    <div className="project-timeline">
      {/* Header with actions */}
      <div className="timeline-header d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0">
            <i className="bi bi-clock-history me-2"></i>
            Bitácora del Proyecto
          </h5>
          {summary && (
            <small className="text-muted">
              ({summary.total_events || 0} eventos
              {summary.total_milestones > 0 && `, ${summary.total_milestones} hitos`})
            </small>
          )}
        </div>

        <div className="d-flex gap-2 align-items-center">
          <ButtonGroup size="sm" aria-label="Cambiar vista">
            <Button
              variant={viewMode === 'feed' ? 'primary' : 'outline-secondary'}
              onClick={() => setViewMode('feed')}
              title="Vista feed (mural)"
            >
              <i className="bi bi-view-stacked me-1"></i>
              <span className="d-none d-md-inline">Feed</span>
            </Button>
            <Button
              variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
              onClick={() => setViewMode('list')}
              title="Vista lista (auditoría)"
            >
              <i className="bi bi-list-ul me-1"></i>
              <span className="d-none d-md-inline">Lista</span>
            </Button>
          </ButtonGroup>
          {canAddEntries && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAddModal(true)}
            >
              <i className="bi bi-plus-circle me-1"></i>
              <span className="d-none d-md-inline">Agregar Nota</span>
              <span className="d-md-none">Agregar</span>
            </Button>
          )}
          <TimelineExportMenu
            project={project}
            events={filteredEvents}
            disabled={filteredEvents.length === 0}
          />
        </div>
      </div>

      {/* Search bar */}
      <TimelineSearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Buscar en la bitácora..."
      />

      {/* Filters */}
      <TimelineFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Timeline content */}
      {filteredEvents.length === 0 ? (
        <div className="timeline-empty">
          <div className="timeline-empty-icon">
            <i className="bi bi-clock-history"></i>
          </div>
          {searchTerm || filters.eventTypes || filters.startDate || filters.milestonesOnly ? (
            <>
              <h5>No se encontraron eventos</h5>
              <p>Intenta ajustar los filtros o el termino de busqueda.</p>
              <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                <i className="bi bi-x-circle me-1"></i>
                Limpiar Filtros
              </Button>
            </>
          ) : (
            <>
              <h5>La bitácora está vacía</h5>
              <p>Los eventos del proyecto aparecerán aquí automáticamente.</p>
              {canAddEntries && (
                <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                  <i className="bi bi-plus-circle me-1"></i>
                  Agregar Primera Nota
                </Button>
              )}
            </>
          )}
        </div>
      ) : viewMode === 'feed' ? (
        <BitacoraFeed events={filteredEvents} />
      ) : (
        <div className="timeline-content">
          {groupedEvents.map(group => (
            <div key={group.date} className="timeline-date-group">
              <div className="timeline-date-header">
                {group.displayDate}
              </div>
              {group.events.map(event => (
                <TimelineEvent
                  key={`${event.source}-${event.id}`}
                  event={event}
                  isHighlighted={!!highlightedEventId && event.id === highlightedEventId}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Add Manual Event Modal */}
      <AddManualEventModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        projectId={projectId}
        onSuccess={handleAddSuccess}
      />
    </div>
  );
};

/**
 * Format date for group headers
 */
const formatDateHeader = (date) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

  if (dateOnly.getTime() === todayOnly.getTime()) {
    return 'Hoy';
  }

  if (dateOnly.getTime() === yesterdayOnly.getTime()) {
    return 'Ayer';
  }

  return date.toLocaleDateString('es-CR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export default ProjectTimeline;
